import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { useTheme } from '../../context/ThemeContext';
import ModeToggle from './ModeToggle';
import { NAV_ITEMS, ANALYTICS_ITEMS } from './navConfig';
import useMediaQuery from '../../hooks/useMediaQuery';
import ModeNudge from './ModeNudge';

// A single sidebar link. Highlights when its route is the active one.
const SideItem = ({ to, icon, label }) => {
  const { COLORS } = useTheme();
  const navigate = useNavigate();
  const location = useLocation();
  const active = location.pathname === to;

  return (
    <button
      onClick={() => navigate(to)}
      style={{
        display: 'flex', alignItems: 'center', gap: 10, width: '100%',
        padding: '10px 14px', borderRadius: 8, border: 'none', cursor: 'pointer',
        background: active ? COLORS.accentGlow : 'transparent',
        color: active ? COLORS.accent : COLORS.textMuted,
        fontFamily: 'inherit', fontSize: 14, fontWeight: 600, textAlign: 'left',
      }}
    >
      <span style={{ fontSize: 18 }}>{icon}</span>
      <span>{label}</span>
    </button>
  );
};

// The analytical/desktop experience: a fixed left sidebar + a wide content area.
const DesktopLayout = ({ children }) => {
  const { COLORS } = useTheme();
  const { user } = useAuth();
  const isNarrow = useMediaQuery('(max-width: 768px)');

  return (
    <div style={{ background: COLORS.bg, fontFamily: "'DM Sans', sans-serif", paddingTop: 'env(safe-area-inset-top)' }}>
      {/* On phone-sized screens, gently steer toward Simple mode */}
      {isNarrow && <ModeNudge />}
      <div style={{ display: 'flex', minHeight: 'calc(100vh - env(safe-area-inset-top))' }}>
      {/* Sidebar */}
      <aside style={{ width: 240, flexShrink: 0, borderRight: `1px solid ${COLORS.border}`, background: COLORS.surface, display: 'flex', flexDirection: 'column', padding: '18px 12px', position: 'sticky', top: 'env(safe-area-inset-top)', height: 'calc(100vh - env(safe-area-inset-top))', overflowY: 'auto' }}>
        <div style={{ padding: '0 8px 18px' }}>
          <span style={{ color: COLORS.text, fontSize: 18, fontWeight: 800, letterSpacing: -0.5 }}>{"💰"} Nest Book</span>
        </div>

        <div style={{ display: 'flex', flexDirection: 'column', gap: 2, marginBottom: 18 }}>
          {NAV_ITEMS.map(item => (
            <SideItem key={item.id} to={`/${item.id}`} icon={item.icon} label={item.label} />
          ))}
        </div>

        <div style={{ color: COLORS.textMuted, fontSize: 10, fontWeight: 700, letterSpacing: 1, textTransform: 'uppercase', padding: '0 14px 6px' }}>
          Analytics
        </div>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          {ANALYTICS_ITEMS.map(item => (
            <SideItem key={item.path} to={item.path} icon={item.icon} label={item.label} />
          ))}
        </div>

        <div style={{ marginTop: 'auto', paddingTop: 18, display: 'flex', flexDirection: 'column', gap: 10 }}>
          <ModeToggle />
          <span style={{ color: COLORS.textMuted, fontSize: 12, padding: '0 6px' }}>Hey, {user?.name?.split(' ')[0]}</span>
        </div>
      </aside>

      {/* Main content */}
      <main style={{ flex: 1, minWidth: 0 }}>
        <div style={{ maxWidth: 820, margin: '0 auto', padding: '8px 0 40px' }}>
          {children}
        </div>
      </main>
      </div>
    </div>
  );
};

export default DesktopLayout;
